/**
 * ⛔ EVERY SECTION BRANCHES ON read_ok BEFORE length.
 *
 * The API returns `read_ok: false` with an empty list when it could not reach
 * Google. Rendering that as "nothing to report" is the failure that told a
 * client they had zero spend in a month they spent $3,953 — the screen showed
 * a result where it should have shown an absence. This exists so no section
 * can forget.
 *
 * Gap A phase 2: it was a private function inside `AdsManagementTab`, which
 * meant a block could only use it by living in that component. Every block
 * moving out to its own category tab needs it, and the last thing this guard
 * should be is re-implemented per tab, so it moved here unchanged.
 */
import { AlertCircle } from 'lucide-react';
import type React from 'react';

import { AdsWaitState } from '@/components/google-ads/AdsWaitState';
import type { ReadState } from '@/services/googleAdsManagementService';

export function ReadGate({
  state,
  isLoading,
  children,
}: {
  state: ReadState | undefined;
  isLoading: boolean;
  children: React.ReactNode;
}) {
  if (isLoading) {
    return <AdsWaitState stage="Reading from Google Ads" />;
  }
  // ⚠ A failed read is NOT an empty result. Say so, and say what it means.
  if (!state || !state.read_ok) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-coral/40 bg-coral-light/20 p-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-coral-dark" />
        <div className="text-sm">
          <p className="font-medium">Could not read this from Google Ads.</p>
          <p className="text-muted-foreground">
            {state?.message ||
              'This is not the same as "nothing to report". Treat anything below as unknown.'}
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export default ReadGate;
